import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * A caterpillar plot in HTML: one row per member, the point estimate and its
 * 90% credible interval drawn as a bar over a shared axis, the n beside the
 * name, suppressed rows greyed and NEVER reordered into the ranked block.
 *
 * Plain markup rather than Recharts on purpose. The whole point of this figure
 * is the interval and the floor, both of which have to be legible in a unit
 * test (jsdom draws nothing Recharts puts inside its SVG), and the order of the
 * rows is a product rule -- listed members first by effect, flagged members
 * after, unranked -- that a charting library would happily "fix".
 */
export interface IntervalRow {
  key: string;
  name: string;
  slug?: string | null;
  n: number;
  /** Secondary count shown after n, e.g. contested items. */
  nLabel?: string;
  raw?: number | null;
  effect: number | null;
  lower: number | null;
  upper: number | null;
  suppressed: boolean;
}

export function IntervalList({
  rows,
  axisLabel,
  zeroLine = true,
  format,
  rawFormat,
  rawLabel,
  linkTo,
}: {
  rows: IntervalRow[];
  axisLabel: string;
  zeroLine?: boolean;
  format: (value: number) => string;
  rawFormat?: (value: number) => string;
  rawLabel?: string;
  linkTo?: (row: IntervalRow) => string | null;
}) {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const drawable = rows.filter((r) => r.effect !== null && r.lower !== null && r.upper !== null);
  if (drawable.length === 0) return null;
  const lo = Math.min(...drawable.map((r) => r.lower as number), zeroLine ? 0 : Infinity);
  const hi = Math.max(...drawable.map((r) => r.upper as number), zeroLine ? 0 : -Infinity);
  const span = hi - lo || 1;
  const pct = (v: number) => `${((v - lo) / span) * 100}%`;
  // Listed members first by effect, then the flagged ones in the same order
  // they arrived -- never interleaved, never ranked.
  const listed = drawable.filter((r) => !r.suppressed);
  const flagged = drawable.filter((r) => r.suppressed);
  const ordered = [...listed, ...flagged];

  return (
    <div className="space-y-1" data-testid="interval-list">
      <ol className="space-y-1">
        {ordered.map((row) => {
          const href = linkTo ? linkTo(row) : null;
          const tone = row.suppressed ? 'text-muted-foreground' : 'text-foreground';
          const bar = row.suppressed ? 'bg-muted-foreground/40' : 'bg-primary/60';
          const dot = row.suppressed ? 'bg-muted-foreground' : 'bg-primary';
          return (
            <li
              key={row.key}
              className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto] items-center gap-3 text-xs"
              data-suppressed={row.suppressed ? 'true' : 'false'}
            >
              <span className={`truncate ${tone}`}>
                {href ? (
                  <Link to={href} className="underline-offset-4 hover:underline">
                    {row.name}
                  </Link>
                ) : (
                  row.name
                )}
                <span className="ml-1 text-muted-foreground">
                  (n={number.format(row.n)}
                  {row.nLabel ? ` ${row.nLabel}` : ''}
                  {row.raw !== null && row.raw !== undefined && rawFormat
                    ? `; ${rawLabel ?? t('anatel:conselho.severity.raw')} ${rawFormat(row.raw)}`
                    : ''}
                  )
                </span>
              </span>
              <span className="relative h-4 rounded bg-muted/40" aria-hidden>
                {zeroLine ? (
                  <span
                    className="absolute top-0 h-full w-px bg-foreground/50"
                    style={{ left: pct(0) }}
                  />
                ) : null}
                <span
                  className={`absolute top-1.5 h-1 rounded ${bar}`}
                  style={{
                    left: pct(row.lower as number),
                    width: `calc(${pct(row.upper as number)} - ${pct(row.lower as number)})`,
                  }}
                />
                <span
                  className={`absolute top-1 h-2 w-2 -translate-x-1 rounded-full ${dot}`}
                  style={{ left: pct(row.effect as number) }}
                />
              </span>
              <span className={`whitespace-nowrap tabular-nums ${tone}`}>
                {format(row.effect as number)}{' '}
                <span className="text-muted-foreground">
                  [{format(row.lower as number)}, {format(row.upper as number)}]
                </span>
                {row.suppressed ? (
                  <span className="ml-1 text-muted-foreground">
                    {t('anatel:conselho.pairs.belowFloor')}
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
      <p className="text-[11px] text-muted-foreground">{axisLabel}</p>
    </div>
  );
}
